// Auth context + axios interceptor setup (with refresh token rotation).
//
// Iter32: handle the 2FA-aware login envelope `{requires_2fa, temp_token}`,
// and surface 429 rate-limit errors as an amber toast.
// Iter36: sync user.locale/display_locale into the i18n provider on auth.
import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { useStore } from "../store";
import { useT, SUPPORTED_LOCALES, SUPPORTED_DISPLAY_LOCALES } from "../lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "sitroom_token";

// Send credentials on every request so the httpOnly cookies travel too
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

let _onUnauthorized = null;
let _onRateLimited = null;
// Single-flight refresh — multiple 401s during a burst share the same promise
let _refreshPromise = null;

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && config.url && config.url.startsWith(API)) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const isAuthRequest = (config) => {
  const url = config?.url || "";
  return (
    url.includes("/api/auth/login") ||
    url.includes("/api/auth/refresh") ||
    url.includes("/api/auth/logout") ||
    url.includes("/api/auth/2fa/")
  );
};

axios.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config || {};
    const status = err.response && err.response.status;

    // 429 rate-limit handler — surface as an amber toast (iter32)
    if (status === 429 && _onRateLimited) {
      const detail = err.response?.data?.detail;
      const retry = (typeof detail === "object" && detail?.retry_after)
        || err.response?.data?.retry_after
        || parseInt(err.response?.headers?.["retry-after"] || "60", 10);
      _onRateLimited(Number.isFinite(retry) ? retry : 60);
    }

    if (
      status === 401 &&
      !original._retried &&
      !isAuthRequest(original) &&
      original.url &&
      original.url.startsWith(API)
    ) {
      original._retried = true;
      try {
        if (!_refreshPromise) {
          _refreshPromise = axios
            .post(`${API}/auth/refresh`, {})
            .finally(() => {
              _refreshPromise = null;
            });
        }
        const r = await _refreshPromise;
        const newToken = r?.data?.token;
        if (newToken) {
          localStorage.setItem(TOKEN_KEY, newToken);
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newToken}`;
          return axios(original);
        }
      } catch (_) {
        // refresh failed — fall through to logout
      }
      if (_onUnauthorized) _onUnauthorized();
    }
    return Promise.reject(err);
  },
);

export const AuthProvider = ({ children }) => {
  // null = checking, false = anonymous, object = signed in
  const [user, setUser] = useState(null);
  const addToast = useStore((s) => s.addToast);
  const { setLocale, setDisplayLocale, t } = useT();

  // Iter36 — when a user record arrives, push their saved locale + display
  // preferences into the i18n provider so the UI updates without a reload.
  const applyUserPrefs = (u) => {
    if (!u) return;
    if (u.locale && SUPPORTED_LOCALES.includes(u.locale)) setLocale(u.locale);
    if (u.display_locale && SUPPORTED_DISPLAY_LOCALES.includes(u.display_locale)) {
      setDisplayLocale(u.display_locale);
    }
  };

  const clear = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  useEffect(() => {
    _onUnauthorized = () => {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    };
    _onRateLimited = (retry) => {
      addToast({
        type: "warning",
        title: t("toasts.too_many_requests"),
        body: t("toasts.wait_seconds", { n: retry }),
      });
    };
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      // Try silent refresh — refresh cookie may exist from a prior session
      axios
        .post(`${API}/auth/refresh`, {})
        .then((r) => {
          if (r?.data?.token) {
            localStorage.setItem(TOKEN_KEY, r.data.token);
            setUser(r.data.user);
            applyUserPrefs(r.data.user);
          } else {
            setUser(false);
          }
        })
        .catch(() => setUser(false));
      return;
    }
    axios
      .get(`${API}/auth/me`)
      .then((r) => { setUser(r.data); applyUserPrefs(r.data); })
      .catch(() => {
        // 401 path already triggered refresh attempt; if we land here, refresh
        // also failed and the interceptor cleared the token + setUser(false).
        if (!localStorage.getItem(TOKEN_KEY)) setUser(false);
      });
    return () => {
      _onUnauthorized = null;
      _onRateLimited = null;
    };
  }, [addToast]);

  /**
   * Two-phase login. Returns one of:
   *   { user }                       — full session, user is set
   *   { requires_2fa, temp_token }   — caller must collect 2FA code, then call verify2fa()
   */
  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data?.requires_2fa) {
      return { requires_2fa: true, temp_token: data.temp_token };
    }
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return { user: data.user };
  };

  /** Complete a 2FA login with the 6-digit code or recovery code. */
  const verify2fa = async (code, temp_token) => {
    const { data } = await axios.post(`${API}/auth/2fa/verify`, { code, temp_token });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (_) {
      // ignore — server-side cookie clear is best-effort
    }
    clear();
  };

  // Iter79 — re-fetch /me after a forced password change so the gate releases.
  const refreshMe = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      applyUserPrefs(data);
      return data;
    } catch (_) { return null; }
  };

  return (
    <AuthContext.Provider value={{ user, login, verify2fa, logout, setUser, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
